/**
 * Dashboard.js
 * Main dashboard showing overview and quick actions
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTeam } from "../context/TeamContext";
import DataService from "../services/DataService";
import {
  FaFolder,
  FaRobot,
  FaHistory,
  FaPlus,
  FaChartLine,
} from "react-icons/fa";

export default function Dashboard() {
  const { currentTeam, permissions } = useTeam();
  const [stats, setStats] = useState({
    contextsCount: 0,
    executionsCount: 0,
    recentExecutions: [],
  });
  const [loading, setLoading] = useState(true);

  const dataService = new DataService();

  useEffect(() => {
    if (currentTeam) {
      loadStats();
    }
  }, [currentTeam]);

  const loadStats = async () => {
    try {
      setLoading(true);

      const [contexts, executions] = await Promise.all([
        dataService.getContexts(currentTeam.id),
        dataService.getAgentExecutions(currentTeam.id, 5),
      ]);

      setStats({
        contextsCount: contexts.length,
        executionsCount: executions.length,
        recentExecutions: executions,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Header */}
      <div className="page-header">
        <h1>Dashboard</h1>
        <p className="page-subtitle">Welcome to {currentTeam.teamName}</p>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <FaFolder />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.contextsCount}</div>
            <div className="stat-label">Context Documents</div>
          </div>
          <Link to="/data-next/context-library" className="stat-link">
            View all →
          </Link>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <FaChartLine />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.executionsCount}</div>
            <div className="stat-label">Total Executions</div>
          </div>
          <Link to="/data-next/history" className="stat-link">
            View all →
          </Link>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <FaRobot />
          </div>
          <div className="stat-content">
            <div className="stat-value">1</div>
            <div className="stat-label">Available Agents</div>
          </div>
          <Link to="/data-next/agents" className="stat-link">
            View agents →
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-grid">
          <Link to="/data-next/context-library" className="action-card">
            <FaFolder className="action-icon" />
            <h3>Upload Context</h3>
            <p>Add documents, notes, or screenshots to your library</p>
          </Link>

          {permissions.canWrite && (
            <Link to="/data-next/agents/flowchart" className="action-card">
              <FaRobot className="action-icon" />
              <h3>Generate Flowchart</h3>
              <p>Create AI-powered diagrams from your context</p>
            </Link>
          )}

          <Link to="/data-next/history" className="action-card">
            <FaHistory className="action-icon" />
            <h3>View History</h3>
            <p>Browse your previous diagram generations</p>
          </Link>
        </div>
      </div>

      {/* Recent Executions */}
      {stats.recentExecutions.length > 0 && (
        <div className="recent-executions">
          <div className="section-header">
            <h2>Recent Diagrams</h2>
            <Link to="/data-next/history" className="view-all-link">
              View All →
            </Link>
          </div>
          <div className="executions-list">
            {stats.recentExecutions.map((execution) => (
              <Link
                key={execution.id}
                to={`/data-next/agents/flowchart/${execution.id}`}
                className="execution-card"
              >
                <div className="execution-header">
                  <span className="execution-type">
                    {execution.agentType === "flowchart"
                      ? "Flowchart"
                      : execution.agentType}
                  </span>
                  <span
                    className={`execution-status status-${execution.status}`}
                  >
                    {execution.status}
                  </span>
                </div>
                <div className="execution-prompt">
                  {execution.prompt || "Untitled diagram"}
                </div>
                <div className="execution-meta">
                  <span>
                    {new Date(
                      execution.createdAt?.toDate?.() || execution.createdAt
                    ).toLocaleDateString()}
                  </span>
                  <span>•</span>
                  <span>{execution.contextIds?.length || 0} contexts</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {stats.contextsCount === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>Get Started with Data-Next</h2>
          <p>
            Upload your first context document to begin generating AI-powered
            diagrams
          </p>
          <Link to="/data-next/context-library" className="btn btn-primary">
            <FaPlus /> Upload Context
          </Link>
        </div>
      )}
    </div>
  );
}
